/**
 * Clipboard utility with cross-browser support.
 *
 * The modern Clipboard API (navigator.clipboard) has restrictions:
 * - Requires HTTPS (except localhost)
 * - Requires user gesture (click, keypress, etc.)
 * - Safari has additional restrictions in certain contexts
 *
 * This utility provides fallbacks for older browsers and Safari edge cases.
 *
 * Cross-browser compatibility:
 * - Chrome: Full Clipboard API support
 * - Firefox: Full Clipboard API support
 * - Safari 13.1+: Clipboard API with restrictions
 * - Safari < 13.1: execCommand fallback
 * - iOS Safari: Clipboard API with gesture requirement
 * - Edge: Full Clipboard API support
 */

import { toast } from 'react-toastify';
import { isIOS } from './browser';

/**
 * Copy text to clipboard with fallback for older browsers.
 *
 * @param text - The text to copy to clipboard
 * @returns Promise resolving to true if successful, false otherwise
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Try modern Clipboard API first
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      // Clipboard API failed (permissions, not in user gesture, etc.)
      // Fall through to legacy method
      console.warn('[Clipboard] Clipboard API failed, trying fallback:', e);
    }
  }

  // Fallback: execCommand (deprecated but widely supported)
  return copyWithExecCommand(text);
}

/**
 * Legacy clipboard copy using execCommand.
 * Works in older browsers and some Safari edge cases.
 *
 * @param text - The text to copy
 * @returns true if successful
 */
function copyWithExecCommand(text: string): boolean {
  // Create a temporary textarea element
  const textarea = document.createElement('textarea');
  textarea.value = text;

  // Make it invisible but still functional
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';

  // iOS Safari requires the element to be editable and in viewport
  textarea.setAttribute('readonly', ''); // Prevent keyboard on mobile
  textarea.style.fontSize = '16px'; // Prevent zoom on iOS

  document.body.appendChild(textarea);

  try {
    // iOS Safari specific: need to select using setSelectionRange
    if (isIOS()) {
      textarea.contentEditable = 'true';
      textarea.readOnly = false;

      const range = document.createRange();
      range.selectNodeContents(textarea);

      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }

      textarea.setSelectionRange(0, text.length);
    } else {
      textarea.select();
    }

    const success = document.execCommand('copy');
    return success;
  } catch (e) {
    console.warn('[Clipboard] execCommand fallback failed:', e);
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * Read text from clipboard.
 * Note: This requires explicit permission and may not work in all contexts.
 *
 * @returns Promise resolving to clipboard text or null if unavailable
 */
export async function readFromClipboard(): Promise<string | null> {
  if (!navigator.clipboard?.readText) {
    console.warn('[Clipboard] Clipboard read not supported');
    return null;
  }

  try {
    return await navigator.clipboard.readText();
  } catch (e) {
    console.warn('[Clipboard] Failed to read clipboard:', e);
    return null;
  }
}

/**
 * Check if clipboard API is available.
 * Note: Availability doesn't guarantee it will work (permissions, context matter).
 */
export function isClipboardAvailable(): boolean {
  return !!navigator.clipboard?.writeText;
}

/**
 * Copy text with user feedback.
 * Returns a message suitable for displaying to the user.
 *
 * @param text - The text to copy
 * @param itemName - Optional name of the item being copied (for messages)
 * @returns Object with success status and message
 */
export async function copyWithFeedback(
  text: string,
  itemName = 'Text',
): Promise<{ success: boolean; message: string }> {
  const success = await copyToClipboard(text);

  if (success) {
    return {
      success: true,
      message: `${itemName} copied to clipboard`,
    };
  }

  return {
    success: false,
    message: `Failed to copy ${itemName.toLowerCase()}. Please copy manually.`,
  };
}

/**
 * Copy, and never fail silently.
 *
 * On success returns true and leaves the "Copied" affordance to the caller
 * (each button has its own). On failure it SAYS so, in a toast that stays up
 * and carries the text itself, so it can still be copied by hand — the
 * previous callers either swallowed the refusal in an empty catch or checked
 * the boolean only on the success branch, so a denied copy looked exactly like
 * a click that did nothing (the admin health dashboard's "Copy info" bug,
 * 2026-09-24, was the same failure on the server-rendered side).
 *
 * @param text - What to copy
 * @param itemName - Named in the failure message ("invite link", "address")
 */
export async function copyOrShow(text: string, itemName = 'text'): Promise<boolean> {
  const ok = await copyToClipboard(text);
  if (!ok) {
    toast.error(`Couldn't copy the ${itemName} automatically. Copy it by hand: ${text}`, {
      autoClose: 15000,
      closeOnClick: false,
    });
  }
  return ok;
}
